import { useCallback, useState } from 'react';
import { LOGIN_NOT_CONFIGURED_MESSAGE } from './authUtilities';
import { useAuthStatus } from './useAuthStatus';

export interface UseLoginResult {
	/**
	 * Sends the user to login. Does nothing if the user is already logged in. Use `useAuthStatus` to read the login
	 * status; this only acts on it.
	 *
	 * The browser navigates away, and after login the CMS sends the user back to `returnUrl`, with `?login=cancelled`
	 * added if login was cancelled or failed. When login cannot be started, `message` (login not configured) or
	 * `error` (the status check failed) is set instead.
	 */
	login: (returnUrl?: string) => Promise<void>;
	/** Set when login cannot be started for this brand. */
	message: string | null;
	/** Set when the status check failed. */
	error: string | null;
	loading: boolean;
}

export const useLogin = (): UseLoginResult => {
	const { checkStatus, loading, error } = useAuthStatus();
	const [message, setMessage] = useState<string | null>(null);

	const login = useCallback(
		async (returnUrl?: string): Promise<void> => {
			setMessage(null);
			try {
				// The status endpoint is also what returns the login URL, so it has to be asked here.
				const status = await checkStatus(returnUrl);
				if (status.authenticated) {
					return;
				}
				if (!status.loginUrl) {
					setMessage(LOGIN_NOT_CONFIGURED_MESSAGE);
					return;
				}
				// Navigate the whole browser (not fetch or the router): the login happens on Auth0.
				window.location.assign(status.loginUrl);
			} catch {
				// checkStatus has already exposed the failure through `error`.
			}
		},
		[checkStatus]
	);

	return { login, message, error, loading };
};
